/* eslint-disable max-lines-per-function */
import { compact } from 'lodash';
import { convert } from '@js-joda/core';
import { ReactElement } from 'react';

import { AppointmentAdvancePaymentQuery } from '@dreryk/egabinet-appointment-advance-payment-frontend-api';
import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import { VisitSettlementQuery } from '@dreryk/egabinet-financial-settlement-frontend-api';
import { VisitAndAppointmentPrescriptionOnlyView } from '@dreryk/egabinet-visit-frontend-api';

import { refetchCqrsQuery } from '@dreryk/command-query-react-common';
import { Dropdown, MoreIconVertical } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { AdvancePaymentIssueActionLink } from '~libs.desktop.shared.appointment-advance-payment';
import { useAppointmentNoteCreateDropdownItem } from '~libs.desktop.shared.appointment';

import { prescriptionVisitsTranslationKeys } from '../../../../translations';
import { usePrescriptionVisitAdvancePaymentGlobalState } from '../../settlement/usePrescriptionVisitAdvancePaymentGlobalState';
import { usePrescriptionVisitSettlementGlobalState } from '../../settlement/usePrescriptionVisitSettlementGlobalState';
import { isAdvancePaymentVisible } from '../PrescriptionVisitSettlementCell.utils';

import { AppointmentCancelDropdownItem } from './AppointmentCancelDropdownItem';
import { PatientFileDropdownItem } from './PatientFileDropdownItem';
import { PatientHistoryDropdownItem } from './PatientHistoryDropdownItem';
import { RefundAdvancePaymentDropdownItem } from './RefundAdvancePaymentDropdownItem';
import { VisitCancelDropdownItem } from './VisitCancelDropdownItem';
import { useVisitReopenDropdownItem } from './useVisitReopenDropdownItem';
import { usePrescriptionVisitRestoreDropdownItem } from './usePrescriptionVisitRestoreDropdownItem';
import { usePrescriptionVisitSettlementDropdownOption } from './usePrescriptionVisitSettlementDropdownOption';
import { usePrescriptionVisitInvoiceCreateDropdownItem } from './usePrescriptionVisitInvoiceCreateDropdownItem';

type PrescriptionVisitAdditionalOptionsCellProps = Pick<
   VisitAndAppointmentPrescriptionOnlyView,
   | 'cancellable'
   | 'appointmentId'
   | 'visitId'
   | 'status'
   | 'patientId'
   | 'ownedByCurrentUser'
   | 'reopened'
   | 'practitioner'
   | 'appointmentDate'
   | 'patientFirstName'
   | 'patientLastName'
   | 'note'
   | 'chronicDiseaseExists'
> & {
   readonly refetchPrescriptionVisits: VoidFunction;
   readonly onlinePaymentsIntegrationExists: boolean;
};

type RenderOptionalDropdownItem = (() => ReactElement) | null;
type RenderDropdownItem = () => ReactElement;

const renderAdditionalOptionsList = (
   status: VisitAndAppointmentPrescriptionOnlyView['status'],
   renderAppointmentCancelDropdownItem: RenderOptionalDropdownItem,
   renderVisitCancelDropdownItem: RenderOptionalDropdownItem,
   renderVisitReopenDropdownItem: RenderDropdownItem,
   renderPrescriptionVisitRestoreDropdownItem: RenderDropdownItem,
   renderVisitSettlementDropdownItem: () => ReactElement | null,
   renderInvoiceCreateDropdownItem: RenderOptionalDropdownItem,
   renderShareAdvancePaymentLink: () => ReactElement | null,
   renderRefundAdvancePaymentDropdownItem: () => ReactElement | null,
   renderPatientFileDropdownItem: RenderDropdownItem,
   renderPatientHistoryDropdownItem: RenderOptionalDropdownItem,
   renderAppointmentNoteCreateDropdownItem: RenderOptionalDropdownItem
   // eslint-disable-next-line max-params
): ReactElement[] => {
   const optionsList: Array<ReactElement | null> = [];

   if (renderAppointmentNoteCreateDropdownItem) {
      optionsList.push(renderAppointmentNoteCreateDropdownItem());
   }

   switch (status) {
      case 'REGISTERED':
         if (renderAppointmentCancelDropdownItem) {
            optionsList.push(renderAppointmentCancelDropdownItem());
         }
         break;
      case 'IN_PROGRESS':
      case 'FINISHED_NOT_APPROVED':
         if (renderVisitCancelDropdownItem) {
            optionsList.push(renderVisitCancelDropdownItem());
         }
         break;
      case 'FINISHED_APPROVED':
         optionsList.push(renderVisitReopenDropdownItem());
         break;
      case 'CANCELLED':
         optionsList.push(renderPrescriptionVisitRestoreDropdownItem());
         break;
      default:
         break;
   }

   optionsList.push(renderVisitSettlementDropdownItem());

   if (renderInvoiceCreateDropdownItem) {
      optionsList.push(renderInvoiceCreateDropdownItem());
   }

   optionsList.push(renderShareAdvancePaymentLink());

   optionsList.push(renderRefundAdvancePaymentDropdownItem());

   optionsList.push(renderPatientFileDropdownItem());

   if (renderPatientHistoryDropdownItem) {
      optionsList.push(renderPatientHistoryDropdownItem());
   }

   return compact(optionsList);
};

export const PrescriptionVisitAdditionalOptionsCell = (
   props: PrescriptionVisitAdditionalOptionsCellProps
): JSX.Element => {
   const patientId = props.patientId.asNumber();
   const appointmentId = props.appointmentId.asNumber();
   const visitId = props.visitId?.asNumber();
   const appointmentDate = convert(props.appointmentDate.atStartOfDay()).toDate();

   const privilegeToCancel = protectFeature('searchingAllAppointments', true, props.ownedByCurrentUser);

   const { getAdvancePayment } = usePrescriptionVisitAdvancePaymentGlobalState();
   const advancePayment = getAdvancePayment(appointmentId);
   const shareAdvancePaymentLinkVisible = isAdvancePaymentVisible(
      advancePayment,
      props.onlinePaymentsIntegrationExists
   );

   const { getSettlement } = usePrescriptionVisitSettlementGlobalState();
   const refundableAdvancePayment = getSettlement(appointmentId)?.advancePayments?.[0];

   const handleRefetchWithPayments = (): void => {
      refetchCqrsQuery(VisitSettlementQuery);
      refetchCqrsQuery(AppointmentAdvancePaymentQuery);
      props.refetchPrescriptionVisits();
   };

   const { VisitReopenDropdownItem, visitReopenModal } = useVisitReopenDropdownItem({ visitId: visitId! });

   const {
      PrescriptionVisitRestoreDropdownItem,
      prescriptionVisitRestoreModal,
   } = usePrescriptionVisitRestoreDropdownItem({ appointmentId: appointmentId });

   const {
      renderVisitSettlementDropdownItem,
      visitSettlementPaymentModal,
      visitSettlementAdjustmentModal,
   } = usePrescriptionVisitSettlementDropdownOption({
      appointmentId: appointmentId,
      practitionerId: props.practitioner.id,
      ownedByCurrentUser: props.ownedByCurrentUser,
      visitDetails: {
         beginDate: appointmentDate,
         endDate: appointmentDate,
         patient: {
            id: patientId,
            firstName: props.patientFirstName?.asString() ?? null,
            lastName: props.patientLastName?.asString() ?? null,
         },
      },
   });

   const { InvoiceCreateDropdownItem, invoiceCreateModal } = usePrescriptionVisitInvoiceCreateDropdownItem({
      patientId: patientId,
      appointmentId: appointmentId,
      visitDate: appointmentDate,
   });

   const { AppointmentNoteCreateDropdownItem, appointmentNoteCreateModal } = useAppointmentNoteCreateDropdownItem({
      appointmentId: appointmentId,
      onAppointmentNoteAdded: props.refetchPrescriptionVisits,
   });

   return (
      <>
         <Dropdown
            triggerIcon={<MoreIconVertical />}
            triggerIconSuffixHidden
            disabled={'empty'}
            testId={'additionalOptions'}
         >
            {renderAdditionalOptionsList(
               props.status,
               protectFeature(
                  'creatingAppointments',
                  () => (
                     <AppointmentCancelDropdownItem
                        key={'appointmentCancel'}
                        appointmentId={appointmentId}
                        refetchPrescriptionVisits={handleRefetchWithPayments}
                        label={prescriptionVisitsTranslationKeys.cancelPlannedAppointment}
                        disabled={!privilegeToCancel}
                     />
                  ),
                  null
               ),
               protectFeature(
                  'creatingVisits',
                  () => {
                     const enabled =
                        !props.reopened && props.ownedByCurrentUser && props.cancellable && !props.chronicDiseaseExists;

                     return (
                        <VisitCancelDropdownItem
                           key={'visitCancel'}
                           visitId={props.visitId}
                           label={prescriptionVisitsTranslationKeys.cancel}
                           refetchPrescriptionVisits={handleRefetchWithPayments}
                           chronicDiseaseExists={props.chronicDiseaseExists}
                           enabled={enabled}
                           reopened={props.reopened}
                        />
                     );
                  },
                  null
               ),
               () => (
                  <VisitReopenDropdownItem
                     key={'visitReopen'}
                     patientId={patientId}
                     visitId={visitId!}
                     ownedByCurrentUser={props.ownedByCurrentUser}
                     appointmentId={appointmentId}
                  />
               ),
               () => (
                  <PrescriptionVisitRestoreDropdownItem key={'prescriptionVisitRestore'} />
               ),
               renderVisitSettlementDropdownItem,
               protectFeature('creatingInvoices', () => <InvoiceCreateDropdownItem key={'invoiceCreate'} />, null),
               () =>
                  shareAdvancePaymentLinkVisible && advancePayment ? (
                     <AdvancePaymentIssueActionLink
                        key={'shareAdvancePaymentLink'}
                        variant={'dropdownItem'}
                        appointmentId={AppointmentId.of(appointmentId)}
                        paidAmount={advancePayment.paidAmount}
                        requiredAmount={advancePayment.requiredAmount}
                        remainingAmount={advancePayment.remainingAmount}
                        refundToBeCreatedAmount={advancePayment.refundToBeCreatedAmount}
                        pendingOnlinePayments={advancePayment.pendingOnlinePayments}
                     />
                  ) : null,
               () =>
                  refundableAdvancePayment ? (
                     <RefundAdvancePaymentDropdownItem
                        key={'refundAdvancePayment'}
                        appointmentId={appointmentId}
                        advancePayment={refundableAdvancePayment}
                        beginDate={appointmentDate}
                        endDate={appointmentDate}
                        patientFirstName={props.patientFirstName?.asString() ?? null}
                        patientLastName={props.patientLastName?.asString() ?? null}
                        onRefundSuccess={handleRefetchWithPayments}
                     />
                  ) : null,
               () => (
                  <PatientFileDropdownItem key={'patientFile'} patientId={patientId} />
               ),
               protectFeature(
                  'searchingMedicalData',
                  () => <PatientHistoryDropdownItem key={'patientHistory'} patientId={patientId} />,
                  null
               ),
               props.note ? null : () => <AppointmentNoteCreateDropdownItem key={'appointmentNoteCreate'} />
            )}
         </Dropdown>
         {visitSettlementPaymentModal}
         {visitSettlementAdjustmentModal}
         {visitReopenModal}
         {invoiceCreateModal}
         {prescriptionVisitRestoreModal}
         {appointmentNoteCreateModal}
      </>
   );
};
